import { CodeBlock } from '@ui/pages/onboarding/components/configuration/CodeBlock'

import { ConfigurationProps } from './types'

export const NewRelicConfiguration = ({ instanceUrl }: ConfigurationProps) => (
  <>
    <h1 className="my-4 text-3xl font-bold">Sending New Relic data to Datable</h1>

    <h2 className="my-4 text-2xl font-bold">New Relic</h2>

    <p className="my-4 text-gray-500">
      Datable supports ingesting directly from New Relic application agents. To support the transition of customers from
      sending data directly to New Relic, Datable will default to proxying all New Relic data directly to New Relic,
      ensuring that data flow is not interrupted during initial onboarding and testing.
    </p>

    <p className="my-4 text-gray-500">
      Redirecting data is configured by either environment variables or configuration.
    </p>

    <h3 className="my-4 text-xl font-bold">Environment variables:</h3>

    <CodeBlock
      code={`
NEW_RELIC_HOST: ${instanceUrl}
NEW_RELIC_PORT: 5401
`}
    />

    <h3 className="my-4 text-xl font-bold">Configuration file variables:</h3>

    <p className="my-4 text-gray-500">
      Both <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">host</code> and{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">port</code> need to be set in your{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">newrelic.yml</code> file.
    </p>

    <CodeBlock
      code={`
host: ${instanceUrl}
port: 5401
`}
    />

    <p className="my-4 text-gray-500">
      If you’re using the New Relic agent, you can update your existing configuration with these variables. The most
      common places for these configurations is the{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">newrelic-infra.yml</code> or environment
      variables in the system your agent is running on, such as your Docker container, Kubernetes container, or bare EC2
      instance. For more information on where to configure these settings, please review the{' '}
      <a
        href="https://docs.newrelic.com/docs/infrastructure/install-infrastructure-agent/configuration/configure-infrastructure-agent/"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        New Relic infrastructure agent configuration documentation.
      </a>
    </p>

    <p className="my-4 text-gray-500">
      If you’re using New Relic APM agents, these settings can be configured in your New Relic configuration file (
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">newrelic.yml</code>,{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">newrelic.js</code>,{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">newrelic.config</code>, etc.) or via environment
      variables on your application container. For configuration information specific to your application’s language,
      see the{' '}
      <a
        href="https://docs.newrelic.com/docs/new-relic-solutions/new-relic-one/install-configure/configure-new-relic-agents/"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        New Relic APM agents configurations
      </a>
      .
    </p>

    <h3 className="my-4 text-xl font-bold">Full example</h3>

    <CodeBlock
      code={`
#
# This file configures the New Relic Agent.  New Relic monitors Ruby, Java,
# .NET, PHP, Python, Node, and Go applications with deep visibility and low
# overhead.  For more information, visit www.newrelic.com.
#
# Generated October 28, 2022
#
# This configuration file is custom generated for NewRelic Administration
#
# For full documentation of agent configuration options, please refer to
# https://docs.newrelic.com/docs/agents/ruby-agent/installation-configuration/ruby-agent-configuration

common: &default_settings
  # Required license key associated with your New Relic account.
  license_key: 'ABC123'

  # Send traffic to Datable
  host: ${instanceUrl}
  port: 5401

  # Your application name. Renaming here affects where data displays in New
  # Relic.  For more details, see https://docs.newrelic.com/docs/apm/new-relic-apm/maintenance/renaming-applications
  app_name: 'whataloaf'

  distributed_tracing:
    enabled: true

  # To disable the agent regardless of other settings, uncomment the following:

  # agent_enabled: false

  # Logging level for log/newrelic_agent.log
  log_level: info

  application_logging:
    # If \`true\`, all logging-related features for the agent can be enabled or disabled
    # independently. If \`false\`, all logging-related features are disabled.
    enabled: true
    forwarding:
      # If \`true\`, the agent captures log records emitted by this application.
      enabled: true
      # Defines the maximum number of log records to buffer in memory at a time.
      max_samples_stored: 10000
    metrics:
      # If \`true\`, the agent captures metrics related to logging for this application.
      enabled: true
    local_decorating:
      # If \`true\`, the agent decorates logs with metadata to link to entities, hosts, traces, and spans.
      # This requires a log forwarder to send your log files to New Relic.
      # This should not be used when forwarding is enabled.
      enabled: false

# Environment-specific settings are in this section.
# RAILS_ENV or RACK_ENV (as appropriate) is used to determine the environment.
# If your application has other named environments, configure them here.
development:
  <<: *default_settings
  app_name: 'whataloaf (Development)'

test:
  <<: *default_settings
  # It doesn't make sense to report to New Relic from automated test runs.
  monitor_mode: false

staging:
  <<: *default_settings
  app_name: 'whataloaf (Staging)'

production:
  <<: *default_settings
`}
    />
  </>
)
